/**
 * The settings screen's icons, ported path-for-path from the Android vector
 * drawables (`ic_credit`, `ic_circle_user`, `ic_transparency`,
 * `ic_notification`, `ic_support_green`, `ic_arrow_left_thin`, `ic_edit`) so
 * the two apps draw the same line at the same weight.
 *
 * Android has no screen for «دسترسی بانی» — the banker role is PWA-only — so
 * `AccessIcon` is drawn fresh in the same hand: a 1.8 green stroke over a 15%
 * leaf-green fill.
 */

type IconProps = { className?: string }

const STROKE = '#74B743'
const TINT = '#91BC68'
const line = {
    fill: 'none',
    stroke: STROKE,
    strokeWidth: 1.8111,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
}
const wash = { fill: TINT, fillOpacity: 0.15 }

export function CreditIcon({ className }: IconProps) {
    return (
        <svg viewBox="0 0 21 16" className={className} aria-hidden="true">
            <path d="M1,5h19v10h-19z" fill="#EFF5E9" fillOpacity={0.2} />
            <path
                {...line}
                d="M2.583,1H18.417C19.292,1 20,1.696 20,2.555V13.445C20,14.304 19.292,15 18.417,15H2.583C1.708,15 1,14.304 1,13.445V2.555C1,1.696 1.708,1 2.583,1Z"
            />
            <path {...line} d="M1,5H20" />
            <path {...line} d="M4,9L8,9" />
        </svg>
    )
}

export function CircleUserIcon({ className }: IconProps) {
    return (
        <svg viewBox="0 0 25 22" className={className} aria-hidden="true">
            <path
                {...wash}
                d="M12.075,0C5.41,0 0,5.399 0,12.064C0,15.485 1.455,18.751 3.999,21.05C5.543,18.006 8.664,16.096 12.075,16.096C9.298,16.096 7.043,13.841 7.043,11.064C7.043,8.287 9.298,6.032 12.075,6.032C14.852,6.032 17.107,8.287 17.107,11.064C17.107,13.841 14.852,16.096 12.075,16.096C15.485,16.096 18.606,18.006 20.15,21.05C25.104,16.596 25.515,8.964 21.061,3.999C18.762,1.455 15.496,0 12.075,0Z"
            />
            <path
                {...line}
                d="M12.075,16.096C14.854,16.096 17.107,13.843 17.107,11.064C17.107,8.285 14.854,6.032 12.075,6.032C9.296,6.032 7.043,8.285 7.043,11.064C7.043,13.843 9.296,16.096 12.075,16.096Z"
            />
            <path
                {...line}
                d="M3.999,21.05C6.265,16.596 11.708,14.807 16.174,17.073C17.884,17.94 19.273,19.339 20.15,21.05"
            />
        </svg>
    )
}

export function TransparencyIcon({ className }: IconProps) {
    const pupil =
        'M10.73,13.17C13.232,13.17 15.26,11.142 15.26,8.64C15.26,6.138 13.232,4.11 10.73,4.11C8.228,4.11 6.2,6.138 6.2,8.64C6.2,11.142 8.228,13.17 10.73,13.17Z'
    return (
        <svg viewBox="0 0 22 18" className={className} aria-hidden="true">
            <path
                {...line}
                d="M0.91,6.95C2.69,3.35 6.43,0.88 10.73,0.91C10.76,0.91 10.79,0.91 10.82,0.91C15.08,0.91 18.77,3.37 20.55,6.95"
            />
            <path
                {...line}
                d="M20.55,10.34C18.77,13.94 15.03,16.41 10.73,16.38C10.7,16.38 10.67,16.38 10.64,16.38C6.38,16.38 2.69,13.92 0.91,10.34"
            />
            <path {...wash} d={pupil} />
            <path {...line} d={pupil} />
        </svg>
    )
}

export function NotificationIcon({ className }: IconProps) {
    const bell =
        'M6.36,11.77C6.35,7.28 9.99,3.63 14.49,3.62C14.51,3.62 14.53,3.62 14.55,3.62C19.03,3.65 22.62,7.38 22.62,11.87V12.67C22.62,16.72 23.47,19.08 24.22,20.37C24.47,20.8 24.32,21.36 23.89,21.61C23.75,21.69 23.6,21.73 23.44,21.73H5.55C5.05,21.73 4.65,21.32 4.65,20.82C4.65,20.66 4.69,20.51 4.77,20.37C5.52,19.08 6.37,16.73 6.37,12.67L6.36,11.77Z'
    return (
        <svg viewBox="0 0 29 29" className={className} aria-hidden="true">
            <path {...wash} d={bell} />
            <path {...line} d="M10.87,25.36H18.11" />
            <path {...line} d={bell} />
        </svg>
    )
}

export function SupportIcon({ className }: IconProps) {
    return (
        <svg viewBox="0 0 24 26" className={className} aria-hidden="true">
            <path
                {...wash}
                d="M1.01,11.87V18.21C1.01,19.21 1.82,20.02 2.82,20.02H4.63C5.63,20.02 6.44,19.21 6.44,18.21V13.68C6.44,12.68 5.63,11.87 4.63,11.87H1.01Z"
            />
            <path
                {...wash}
                d="M22.91,11.87H19.29C18.29,11.87 17.48,12.68 17.48,13.68V18.21C17.48,19.21 18.29,20.02 19.29,20.02H21.1C22.1,20.02 22.91,19.21 22.91,18.21V11.87Z"
            />
            <path
                {...line}
                d="M22.91,11.87H19.29C18.29,11.87 17.48,12.68 17.48,13.68V18.21C17.48,19.21 18.29,20.02 19.29,20.02H21.1C22.1,20.02 22.91,19.21 22.91,18.21V11.87ZM22.91,11.87C22.91,5.87 18.05,1 12.04,1C12.01,1 11.98,1 11.95,1C5.95,0.96 1.05,5.79 1,11.79C1,11.82 1,11.84 1,11.87V18.21C1,19.21 1.81,20.02 2.81,20.02H4.62C5.62,20.02 6.43,19.21 6.43,18.21V13.68C6.43,12.68 5.62,11.87 4.62,11.87H1.01"
            />
            <path
                {...line}
                d="M22.91,18.21V20.93C22.91,22.93 21.29,24.55 19.29,24.55H12.78"
            />
        </svg>
    )
}

/** A shield with a tick — who may see and record what. */
export function AccessIcon({ className }: IconProps) {
    const shield =
        'M11,1.2L19.6,4.4V10.6C19.6,15.9 16,20.4 11,22.2C6,20.4 2.4,15.9 2.4,10.6V4.4Z'
    return (
        <svg viewBox="0 0 22 24" className={className} aria-hidden="true">
            <path {...wash} d={shield} />
            <path {...line} d={shield} />
            <path {...line} d="M7.4,11.6L10,14.1L14.6,9.2" />
        </svg>
    )
}

/** Android's thin chevron; takes its colour from `currentColor`. */
export function ArrowLeftThinIcon({ className }: IconProps) {
    return (
        <svg viewBox="0 0 7 10" className={className} aria-hidden="true">
            <path
                d="M5.664,0.664L1.664,4.664L5.664,8.664"
                fill="none"
                stroke="currentColor"
                strokeWidth={1}
            />
        </svg>
    )
}

/** Android's pencil; takes its colour from `currentColor`. */
export function PencilIcon({ className }: IconProps) {
    const pen = {
        ...line,
        stroke: 'currentColor',
    }
    return (
        <svg viewBox="0 0 16 16" className={className} aria-hidden="true">
            <path {...pen} d="M8.325,3.191L12.597,7.463" />
            <path
                {...pen}
                d="M5.023,14.788H1.613C1.276,14.788 1,14.512 1,14.175V10.765C1,10.603 1.061,10.448 1.175,10.34L10.333,1.19C10.569,0.947 10.953,0.947 11.195,1.183L11.202,1.19L14.605,4.592C14.848,4.828 14.848,5.212 14.612,5.455L14.605,5.462L5.447,14.613C5.34,14.727 5.185,14.788 5.023,14.788Z"
            />
        </svg>
    )
}
